import { Button } from '@almbrand/button';
import { Paragraph } from '@almbrand/paragraph';
import { RichText } from '@almbrand/richtext';
import { TrackingEvent } from '@almbrand/trackinghooks';
import { HeadlineComponent, PreviewButton } from 'components/1-atoms';
import { ThemeNames } from 'constantsValues/themeNames';
import { useThemeName } from 'hooks/useThemeName';
import { useState } from 'react';
import styles from './MultiColItemCard.module.scss';

export interface Item {
	id: string; // Unique identifier for the policy/message/other
	title: string; // Main title for the policy/message/other
	subtitle?: string; // Optional subtitle, e.g., address or description
	price?: string; // Optional price, e.g., '8.599 kr/år'
	content_type?: string; // Optional content type, e.g., 'pdf', 'image'
}

export interface MultiColItemCardProps {
	title: string; // Title for the Card
	items: Item[]; // Array of insurance or message data
	onItemClick?: (item: Item) => void; // Generic callback for item click
	onMoreClick?: () => void; // Callback when "+ {count} mere" is clicked
	moreClickDestination?: string; // The destination for the "more" link - tracking purposes
	showError?: boolean; // Status code to handle error scenarios
	errorMessage?: string; // Optional message to display when there is no content
}

export const MultiColItemCard: React.FC<MultiColItemCardProps> = ({
	title,
	items: products,
	onItemClick,
	onMoreClick,
	moreClickDestination,
	showError,
	errorMessage,
}) => {
	const rootUrl = window.location.origin;
	const visibleItems = products?.slice(0, 3); // Show the first 3 items
	const remainingCount = products?.length - 3; // Calculate the remaining count
	const themeName = useThemeName();
	const isAlmbrand = themeName === ThemeNames.almbrand;

	const trackingData: TrackingEvent = {
		event: 'trackNavigation',
		navigationAction: title ?? '',
		navigationDestination: `${rootUrl + moreClickDestination}`,
		navigationType: 'call-to-action',
	};

	const [hoverStyle, setHoverStyle] = useState<Item | null>(null);

	// Handle item click and call the parent callback
	const handleClick = (item: Item) => {
		onItemClick?.(item); // Invoke the callback passed from the parent
	};

	return (
		<div className={styles.MultiColItemCard}>
			<div className={styles.MultiColItemCard__header}>
				{isAlmbrand ? (
					<HeadlineComponent
						title={title}
						heading='h3'
						size='default'
					/>
				) : (
					<Paragraph
						text={title}
						paragraphType={{ kind: 'title', type: 'title' }}
						fontColor='primaryDark'
					/>
				)}
				{!showError && (
					<Button
						buttonType={isAlmbrand ? 'link' : 'tertiary'}
						size={isAlmbrand ? 'large' : 'small'}
						onClick={(e) => {
							e.preventDefault();
							onMoreClick?.();
						}}
						trackingEvent={trackingData}
					>
						Se alle
					</Button>
				)}
			</div>

			{/* Flex container for the list */}
			<div className={styles.MultiColItemCard__listContainer}>
				{showError && (
					<div className={styles.MultiColItemCard__errorContent}>
						<RichText description={errorMessage} />
					</div>
				)}

				{visibleItems &&
					visibleItems?.map((item, index) => (
						<div
							key={index}
							className={styles.MultiColItemCard__listItem}
							onClick={() => handleClick(item)} // Trigger the callback on click
							onMouseEnter={() => setHoverStyle(item)}
							onMouseLeave={() => setHoverStyle(null)}
						>
							{/* Display item details */}
							<div className={styles.MultiColItemCard__listItem__details}>
								<Paragraph
									text={item.title}
									paragraphType={{ kind: 'paragraph', size: 'default' }}
									fontColor={!isAlmbrand && hoverStyle === item ? 'primary' : 'primaryDark'}
								/>
								{item.subtitle && (
									<Paragraph
										text={item.subtitle}
										paragraphType={{ kind: 'paragraph', size: 'default' }}
										fontColor='secondaryDarkGrey'
									/>
								)}
							</div>

							{!isAlmbrand && item.price && (
								<div className={styles.MultiColItemCard__listItem__price}>
									<Paragraph
										text={item.price}
										paragraphType={{ kind: 'paragraph', size: 'default' }}
										fontColor='primaryDark'
									/>
								</div>
							)}

							<PreviewButton />
						</div>
					))}
			</div>

			{!isAlmbrand && !Number.isNaN(remainingCount) && remainingCount > 0 && (
				<div className={styles.MultiColItemCard__moreLink}>
					<Button
						buttonType='link'
						onClick={(e) => {
							e.preventDefault();
							onMoreClick?.();
						}}
						trackingEvent={trackingData}
					>
						{`+ ${remainingCount} mere`}
					</Button>
				</div>
			)}
		</div>
	);
};
